// Copyright (c) 2026 Athan Clark. SPDX-License-Identifier: GPL-3.0-only
// How plugins show in the editor: a moment's marker image, shape and heading, and the
// inspector's inputs for each kind of plugin field (text, images, stacks, colors and the rest).
import { attachRichText } from './rich-text.js';
import { imageSource, imagesOffline } from './image-assets.js';
import {
  imageURL,
  sourceLinks,
  pluginRichText,
  pluginFields,
  readStack,
  stackEntry,
  COLOR_SWATCHES,
  colorValue,
  shapeNames,
  shapeValue,
  MOMENT_SIZES,
  sizeValue,
  HEADING_LEVELS,
  headingValue,
  HEADING_FONTS,
  DEFAULT_HEADING_FONT,
  fontValue,
  toggleValue,
  readRelationshipArrows,
} from './plugins.js';
import type { ArrowDirection, HeadingLevel, OutlineStyle, PluginField } from './plugins.js';
import type { InstalledPlugin, StackEntry } from './plugins.js';
/** What a relationship-arrows field (#326) knows of the entity's relationships. */
export interface RelationshipContext {
  /**
   * The entity's directly related entities: names by reference key ("m:<id>" or "d:<id>"),
   * and whether that is all of them (a very linked entity may list only some).
   */
  related(): Promise<{ entities: Map<string, string>; complete: boolean }>;
  /** An entity's name, when the view has it. */
  name(key: string): string | null;
  /**
   * What the other end's own arrows say of its link to this entity, when its metadata is at
   * hand: an arrowhead at its own end (`self`) or at this entity's end (`other`).
   */
  theirs(key: string): { self: boolean; other: boolean } | null;
}
/**
 * A relationship-arrows field (#326): one row per stored entry, naming the other end of one
 * of the entity's relationships and the arrow chosen for it, with × to remove it. Choosing a
 * related entity from the list adds an entry. An entry whose relationship no longer exists is
 * outlined in red and says so; an unreadable one says that. Both stay as stored until removed,
 * and a value that isn't a list is shown but not edited.
 */
function renderRelationshipArrows(
  section: HTMLElement,
  field: PluginField,
  metadata: Record<string, unknown>,
  editable: boolean,
  onChange: (key: string, value: unknown) => void,
  context: RelationshipContext | undefined,
) {
  const raw = metadata[field.metadataKey];
  const stored: unknown[] = Array.isArray(raw) ? JSON.parse(JSON.stringify(raw)) : [];
  const fixed = raw !== undefined && !Array.isArray(raw);
  const heading = document.createElement('h3');
  heading.textContent = field.label;
  const list = document.createElement('ul');
  list.className = 'relationship-arrows';
  const add = document.createElement('select');
  add.setAttribute('aria-label', 'Add an arrow to a relationship');
  add.dataset.pluginKey = field.metadataKey;
  const hint = document.createElement('p');
  hint.className = 'field-hint';
  section.append(heading, list, add, hint);
  let related: { entities: Map<string, string>; complete: boolean } | null = null;
  const commit = () => onChange(field.metadataKey, stored.length ? stored : '');
  const name = (key: string) =>
    related?.entities.get(key) ||
    context?.name(key) ||
    (key.startsWith('m:') ? 'Moment ' : 'Duration ') + key.slice(2);
  const choices: Record<ArrowDirection, (other: string) => string> = {
    none: () => 'No arrow',
    to: (other) => `This event → ${other}`,
    from: (other) => `${other} → this event`,
    both: (other) => `This event ↔ ${other}`,
  };
  const draw = () => {
    list.replaceChildren();
    const read = readRelationshipArrows(stored);
    const targets = new Set<string>();
    stored.forEach((_, index) => {
      const item = document.createElement('li');
      item.className = 'relationship-arrow';
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.textContent = '×';
      remove.hidden = !editable || fixed;
      remove.onclick = () => {
        stored.splice(index, 1);
        commit();
        draw();
      };
      const entry = read.entries.find((e) => e.index === index);
      if (!entry) {
        item.classList.add('unreadable');
        const note = document.createElement('span');
        note.className = 'field-hint';
        note.textContent = "Unreadable entry: it isn't drawn.";
        remove.setAttribute('aria-label', 'Remove the unreadable entry');
        item.append(note, remove);
        list.append(item);
        return;
      }
      const key =
        'moment' in entry.target ? 'm:' + entry.target.moment : 'd:' + entry.target.duration;
      targets.add(key);
      const other = name(key);
      item.dataset.target = key;
      const label = document.createElement('label');
      label.textContent = other;
      const select = document.createElement('select');
      select.disabled = !editable || fixed;
      for (const arrow of Object.keys(choices) as ArrowDirection[])
        select.append(new Option(choices[arrow](other), arrow));
      select.value = entry.arrow;
      select.onchange = () => {
        (stored[index] as Record<string, unknown>).arrow = select.value;
        commit();
      };
      label.append(select);
      remove.setAttribute('aria-label', `Remove the arrow with ${other}`);
      item.append(label, remove);
      // Only a full list of relationships tells that one is gone.
      if (related?.complete && !related.entities.has(key)) {
        item.classList.add('dangling');
        const note = document.createElement('p');
        note.className = 'field-hint';
        note.textContent = 'No relationship with this entity. It isn’t drawn.';
        item.append(note);
      }
      const theirs = context?.theirs(key);
      if (theirs && (theirs.self || theirs.other)) {
        const note = document.createElement('p');
        note.className = 'field-hint relationship-arrow-theirs';
        note.textContent = `${other} adds ${
          theirs.self && theirs.other
            ? 'arrowheads at both ends'
            : theirs.other
              ? 'an arrowhead at this event'
              : `an arrowhead at ${other}`
        }.`;
        item.append(note);
      }
      list.append(item);
    });
    add.replaceChildren(new Option('Add an arrow…', ''));
    for (const [key, title] of related?.entities ?? [])
      if (!targets.has(key)) add.append(new Option(title, key));
    add.hidden = !editable || fixed;
    add.disabled = add.options.length === 1;
    hint.textContent = fixed
      ? "This value can't be read, so it can't be edited here. Edit it under Additional metadata instead."
      : !related && context
        ? 'Loading relationships…'
        : !stored.length && !related?.entities.size
          ? 'Link this event to another under Related to, then choose an arrow for it here.'
          : '';
  };
  add.onchange = () => {
    const key = add.value;
    if (!key) return;
    stored.push({
      target: key.startsWith('m:') ? { moment: key.slice(2) } : { duration: key.slice(2) },
      arrow: 'to',
    });
    commit();
    draw();
    list.querySelector<HTMLSelectElement>('li:last-child select')?.focus();
  };
  // Relationships change in the Related to section beside this, so they are read again
  // whenever the list of choices is opened.
  const load = () =>
    context
      ?.related()
      .then((found) => {
        related = found;
        draw();
      })
      .catch(() => {});
  add.onfocus = () => void load();
  draw();
  void load();
}

/**
 * An image element for plugin pictures: fetched without credentials or a referrer (the
 * image host learns nothing of the timeline or the person), decoded off the main thread
 * and loaded lazily unless the caller says otherwise.
 */
function image(alt: string) {
  const img = document.createElement('img');
  img.alt = alt;
  img.crossOrigin = 'anonymous';
  img.referrerPolicy = 'no-referrer';
  img.decoding = 'async';
  img.loading = 'lazy';
  return img;
}
/**
 * Draws a marker's content: for a single moment with an image `source` (already resolved to
 * an embedded copy or a URL), that image, shown only once it has loaded (the `icon` class
 * styles the marker for it); otherwise its count, abbreviated above 999 (`1.2k`, `12k`), or
 * nothing for a single moment. Reuses the existing image element and only touches the DOM
 * when something changed, since markers are redrawn on every frame.
 */
export function renderPluginMarker(
  button: HTMLButtonElement,
  source: string | null,
  count: bigint,
) {
  let img = button.querySelector('img');
  if (count !== 1n || !source) {
    if (img) img.remove();
    button.classList.remove('icon');
    const text =
      count > 1n
        ? count > 999n
          ? `${(Number(count) / 1000).toFixed(count < 10000n ? 1 : 0)}k`
          : count.toString()
        : '';
    if (button.textContent !== text) button.textContent = text;
    return;
  }
  if (!img) {
    img = image('');
    img.loading = 'eager'; // Visible marker images must load even while their pending preview is hidden.
    // Decorative: the marker button carries the moment's accessible name.
    img.setAttribute('aria-hidden', 'true');
    button.replaceChildren(img);
    // A late load or error for an image the marker no longer shows is ignored.
    img.onload = () => {
      if (img!.parentElement !== button) return;
      button.classList.add('icon');
      img!.hidden = false;
    };
    img.onerror = () => {
      if (img!.parentElement !== button) return;
      button.classList.remove('icon');
      img!.hidden = true;
    };
  }
  button.classList.toggle('icon', img.complete && img.naturalWidth > 0);
  if (img.getAttribute('src') !== source) {
    img.hidden = true;
    img.src = source;
  }
}
/**
 * Builds the inspector inputs for every field the installed plugins declare (in plugin
 * order) into `container`, from a moment's or stack entry's `metadata`. Each input calls
 * `onChange(metadataKey, value)` as it is edited, with the value already checked where the
 * field has rules (links, fonts, image URLs); the caller stores it. `openImage` opens an
 * image's original, `confirmDelete` asks before removing a stack entry, and `inheritedTime`
 * is the time a stack's entries share. `child` is set for the fields inside a stack entry,
 * which can't hold stacks of their own (nor relationship arrows: they aren't relationship
 * endpoints). `relationships` tells relationship-arrows fields about the entity's links.
 * With `editable` false everything is read-only.
 */
export function renderPluginFields(
  container: HTMLElement,
  plugins: readonly InstalledPlugin[],
  metadata: Record<string, unknown>,
  editable: boolean,
  onChange: (key: string, value: unknown) => void,
  openImage: (event: MouseEvent, url: string) => void,
  confirmDelete: (title: string, remove: () => void) => void,
  inheritedTime: () => string = () => '',
  child = false,
  relationships?: RelationshipContext,
) {
  container.replaceChildren();
  for (const field of pluginFields(plugins)) {
    if ((field.kind === 'stack' || field.kind === 'relationship-arrows') && child) continue;
    const section = document.createElement('section');
    section.className = 'plugin-field';
    section.dataset.metadataKey = field.metadataKey;
    if (field.kind === 'relationship-arrows') {
      renderRelationshipArrows(section, field, metadata, editable, onChange, relationships);
      container.append(section);
      continue;
    }
    if (field.kind === 'links') {
      const heading = document.createElement('label');
      heading.textContent = field.label;
      const list = document.createElement('ul');
      list.className = 'plugin-source-links';
      const input = document.createElement('textarea');
      input.dataset.pluginKey = field.metadataKey;
      input.rows = 3;
      // Up to 100 links of 4,096 characters, each with its newline.
      input.maxLength = 100 * 4097;
      input.spellcheck = false;
      input.disabled = !editable;
      const raw = metadata[field.metadataKey];
      input.value = Array.isArray(raw) ? raw.filter((v) => typeof v === 'string').join('\n') : '';
      const draw = (links: string[]) => {
        list.replaceChildren();
        for (const url of links) {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.textContent = url;
          link.href = url;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.referrerPolicy = 'no-referrer';
          item.append(link);
          list.append(item);
        }
        list.hidden = links.length === 0;
      };
      // Validates the lines as a whole (sourceLinks: HTTPS only, at most 100); an invalid
      // list is reported on the textarea and not saved, so the last valid one stays.
      const update = (commit: boolean) => {
        try {
          const links = sourceLinks(
            input.value
              .split(/\r?\n/)
              .map((s) => s.trim())
              .filter(Boolean),
          );
          input.setCustomValidity('');
          draw(links);
          if (commit) onChange(field.metadataKey, links);
        } catch (error) {
          input.setCustomValidity((error as Error).message);
        }
      };
      input.oninput = () => update(true);
      const hint = document.createElement('p');
      hint.className = 'field-hint';
      hint.textContent = 'One HTTPS source link per line. Links open only when clicked.';
      heading.append(input);
      section.append(list, heading, hint);
      update(false);
      if (!editable) {
        input.hidden = true;
        hint.hidden = true;
      }
      container.append(section);
      continue;
    }
    if (field.kind === 'shape' || field.kind === 'size') {
      const label = document.createElement('label');
      label.textContent = field.label;
      const select = document.createElement('select');
      select.setAttribute('aria-label', field.label);
      select.disabled = !editable;
      select.dataset.pluginKey = field.metadataKey;
      // The shapes of the plugin whose field this is (the last enabled one declaring it).
      const owner = [...plugins]
        .reverse()
        .find((p) => p.enabled && p.manifest.fields.includes(field));
      for (const shape of field.kind === 'size' ? MOMENT_SIZES : shapeNames(owner?.manifest)) {
        const option = document.createElement('option');
        option.value = shape;
        option.textContent = shape[0].toUpperCase() + shape.slice(1);
        select.append(option);
      }
      select.value =
        field.kind === 'size'
          ? (sizeValue(metadata[field.metadataKey]) ?? 'medium')
          : (shapeValue(metadata[field.metadataKey], owner?.manifest) ?? 'circle');
      select.onchange = () => onChange(field.metadataKey, select.value);
      label.append(select);
      section.append(label);
      container.append(section);
      continue;
    }
    if (field.kind === 'heading') {
      const label = document.createElement('label');
      label.textContent = field.label;
      const select = document.createElement('select');
      select.setAttribute('aria-label', field.label);
      select.disabled = !editable;
      select.dataset.pluginKey = field.metadataKey;
      select.append(new Option('Not a heading', ''));
      for (const level of HEADING_LEVELS)
        select.append(new Option(`Heading ${level.slice(1)}`, level));
      select.value = headingValue(metadata[field.metadataKey]) ?? '';
      select.onchange = () => onChange(field.metadataKey, select.value);
      label.append(select);
      section.append(label);
      container.append(section);
      continue;
    }
    if (field.kind === 'font') {
      // Named fonts, or any installed family list; nothing is downloaded.
      const label = document.createElement('label');
      label.textContent = field.label;
      const select = document.createElement('select');
      select.setAttribute('aria-label', field.label);
      select.disabled = !editable;
      select.dataset.pluginKey = field.metadataKey;
      for (const font of HEADING_FONTS) select.append(new Option(font.label, font.value));
      select.append(new Option('Custom…', 'custom'));
      const custom = document.createElement('input');
      custom.type = 'text';
      custom.maxLength = 200;
      custom.spellcheck = false;
      custom.disabled = !editable;
      custom.placeholder = 'Font names, such as Baskerville, serif';
      custom.setAttribute('aria-label', field.label + ' (custom)');
      const hint = document.createElement('p');
      hint.className = 'field-hint';
      const current = fontValue(metadata[field.metadataKey]) ?? DEFAULT_HEADING_FONT;
      const preset = HEADING_FONTS.some((f) => f.value === current);
      select.value = preset ? current : 'custom';
      custom.value = preset ? '' : current;
      const show = () => {
        custom.hidden = select.value !== 'custom';
        hint.textContent = custom.hidden
          ? ''
          : 'Fonts installed on this device, separated by commas. End with serif or sans-serif for devices without them.';
      };
      show();
      select.onchange = () => {
        show();
        if (select.value !== 'custom')
          onChange(field.metadataKey, select.value === DEFAULT_HEADING_FONT ? '' : select.value);
        else custom.focus();
      };
      // A custom family list is saved only when valid (or cleared, meaning the default).
      custom.onchange = () => {
        const value = fontValue(custom.value);
        custom.setCustomValidity(
          value || !custom.value.trim() ? '' : 'Use font names separated by commas.',
        );
        hint.textContent = custom.validationMessage || hint.textContent;
        if (value || !custom.value.trim()) onChange(field.metadataKey, value ?? '');
      };
      label.append(select);
      section.append(label, custom, hint);
      container.append(section);
      continue;
    }
    if (field.kind === 'toggle') {
      // A checkbox (#325): checked stores `true`, unchecked removes the key. Any other stored
      // value reads as off and is kept until the box is changed.
      const label = document.createElement('label');
      label.className = 'plugin-toggle';
      const box = document.createElement('input');
      box.type = 'checkbox';
      box.checked = toggleValue(metadata[field.metadataKey]);
      box.disabled = !editable;
      box.dataset.pluginKey = field.metadataKey;
      box.onchange = () => onChange(field.metadataKey, box.checked);
      label.append(box, document.createTextNode(field.label));
      section.append(label);
      container.append(section);
      continue;
    }
    if (field.kind === 'color') {
      const label = document.createElement('label');
      label.textContent = field.label;
      const picker = document.createElement('input');
      picker.type = 'color';
      picker.value = colorValue(metadata[field.metadataKey]) ?? '#547d5b';
      picker.disabled = !editable;
      picker.dataset.pluginKey = field.metadataKey;
      label.append(picker);
      const swatches = document.createElement('div');
      swatches.className = 'color-swatches';
      const buttons: HTMLButtonElement[] = [];
      // Chooses a colour from a swatch or the picker; the swatch with no value means the
      // default colour, which the picker shows as the default green.
      const select = (value: string) => {
        buttons.forEach((b, i) =>
          b.setAttribute('aria-pressed', String(COLOR_SWATCHES[i].value === value)),
        );
        picker.value = value || '#547d5b';
        onChange(field.metadataKey, value);
      };
      for (const swatch of COLOR_SWATCHES) {
        const button = document.createElement('button');
        button.type = 'button';
        button.disabled = !editable;
        button.textContent = swatch.name;
        button.title = swatch.name;
        button.style.setProperty('--swatch', swatch.value || '#547d5b');
        button.setAttribute(
          'aria-pressed',
          String((colorValue(metadata[field.metadataKey]) ?? '') === swatch.value),
        );
        button.onclick = () => select(swatch.value);
        buttons.push(button);
        swatches.append(button);
      }
      picker.oninput = () => select(picker.value);
      section.append(label, swatches);
      container.append(section);
      continue;
    }
    if (field.kind === 'stack') {
      const stackKeys = plugins.flatMap((p) =>
        p.manifest.fields.filter((f) => f.kind === 'stack').map((f) => f.metadataKey),
      );
      // The stack reads what it can of whatever is stored (#157). Saving writes only the
      // entries it read, so a stack holding others is shown but not edited.
      const read = readStack(metadata[field.metadataKey], stackKeys);
      let entries: StackEntry[] = read.entries;
      const stackEditable = editable && !read.unreadable;
      const heading = document.createElement('h3');
      heading.textContent = field.label;
      const unreadable = document.createElement('p');
      unreadable.className = 'field-hint stack-unreadable';
      unreadable.hidden = !read.unreadable;
      unreadable.textContent = Array.isArray(metadata[field.metadataKey])
        ? `${read.unreadable} ${read.unreadable === 1 ? 'entry' : 'entries'} of this stack can't be read, so the stack can't be edited here. Edit it under Additional metadata instead.`
        : "This stack's value can't be read, so the stack can't be edited here. Edit it under Additional metadata instead.";
      const list = document.createElement('div');
      list.className = 'stack-list';
      const commit = () => onChange(field.metadataKey, entries);
      /** The ID of the entry being dragged, by mouse (HTML drag and drop) or touch/pen. */
      let dragged: string | null = null;
      /** Moves an entry from one index to another, saving and redrawing; bad moves are ignored. */
      const move = (from: number, to: number) => {
        if (
          !stackEditable ||
          from < 0 ||
          to < 0 ||
          from >= entries.length ||
          to >= entries.length ||
          from === to
        )
          return;
        entries.splice(to, 0, entries.splice(from, 1)[0]);
        commit();
        draw();
      };
      /**
       * Rebuilds every entry card: reorder controls (a drag handle and up/down buttons for
       * keyboards), delete, the inherited time, title and notes, the other plugin fields,
       * and the entry's remaining metadata as raw JSON.
       */
      const draw = () => {
        list.replaceChildren();
        entries.forEach((entry, index) => {
          const card = document.createElement('section');
          card.className = 'stack-card';
          card.dataset.stackId = entry.id;
          const actions = document.createElement('div');
          actions.className = 'stack-actions';
          const name = document.createElement('strong');
          name.textContent = `Entry ${index + 1}`;
          actions.append(name);
          const button = (label: string, action: () => void, disabled = !stackEditable) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.textContent = label;
            b.disabled = disabled;
            b.onclick = action;
            actions.append(b);
            return b;
          };
          const handle = button('↕ Drag', () => {});
          handle.draggable = stackEditable;
          handle.ondragstart = (event) => {
            dragged = entry.id;
            event.dataTransfer?.setData('text/plain', entry.id);
            if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
          };
          handle.ondragend = () => {
            dragged = null;
          };
          // Touch and pen don't start HTML drag and drop reliably, so they drag by pointer
          // capture instead and drop on whichever card is under the finger on release.
          handle.onpointerdown = (event) => {
            if (!stackEditable || event.pointerType === 'mouse') return;
            event.preventDefault();
            handle.setPointerCapture(event.pointerId);
            dragged = entry.id;
          };
          handle.onpointerup = (event) => {
            if (event.pointerType === 'mouse' || !dragged) return;
            const target = document
              .elementFromPoint(event.clientX, event.clientY)
              ?.closest<HTMLElement>('.stack-card');
            if (target && list.contains(target))
              move(
                entries.findIndex((e) => e.id === dragged),
                entries.findIndex((e) => e.id === target.dataset.stackId),
              );
            dragged = null;
          };
          handle.onpointercancel = () => {
            dragged = null;
          };
          // Accepting the dragover (preventDefault) is what makes the card a drop target.
          card.ondragover = (event) => {
            if (stackEditable && dragged) event.preventDefault();
          };
          card.ondrop = (event) => {
            if (!dragged || !stackEditable) return;
            event.preventDefault();
            move(
              entries.findIndex((e) => e.id === dragged),
              index,
            );
            dragged = null;
          };
          button('↑', () => move(index, index - 1), !stackEditable || index === 0).ariaLabel =
            'Move stack entry up';
          button(
            '↓',
            () => move(index, index + 1),
            !stackEditable || index === entries.length - 1,
          ).ariaLabel = 'Move stack entry down';
          button('Delete', () =>
            confirmDelete(String(entry.metadata.title || 'Untitled stack entry'), () => {
              // The inspector may have been redrawn while the confirmation was open.
              if (!section.isConnected || !stackEditable) return;
              entries = entries.filter((e) => e.id !== entry.id);
              commit();
              draw();
            }),
          );
          const time = document.createElement('p');
          time.className = 'field-hint stack-time';
          time.textContent = `Inherited time: ${inheritedTime()}`;
          card.append(actions, time);
          for (const [key, labelText] of [
            ['title', 'Title'],
            ['description', 'Notes'],
          ]) {
            const label = document.createElement('label');
            label.textContent = labelText;
            const input =
              key === 'title'
                ? document.createElement('input')
                : document.createElement('textarea');
            input.value = String(entry.metadata[key] ?? '');
            input.disabled = !stackEditable;
            input.oninput = () => {
              entry.metadata[key] = input.value;
              commit();
            };
            label.append(input);
            card.append(label);
            if (key === 'description' && pluginRichText(plugins))
              attachRichText(input as HTMLTextAreaElement, stackEditable);
          }
          // The entry's own plugin fields (no nested stacks); their edits also refresh the
          // raw JSON view below, assigned once that view exists.
          const other = document.createElement('div');
          let syncRaw = () => {};
          renderPluginFields(
            other,
            plugins,
            entry.metadata,
            stackEditable,
            (key, value) => {
              if (value) entry.metadata[key] = value;
              else delete entry.metadata[key];
              syncRaw();
              commit();
            },
            openImage,
            confirmDelete,
            inheritedTime,
            true,
          );
          card.append(other);
          const details = document.createElement('details');
          const summary = document.createElement('summary');
          summary.textContent = 'Additional metadata';
          const raw = document.createElement('textarea');
          raw.disabled = !stackEditable;
          const rest = { ...entry.metadata };
          delete rest.title;
          delete rest.description;
          raw.value = JSON.stringify(rest, null, 2);
          syncRaw = () => {
            const rest = { ...entry.metadata };
            delete rest.title;
            delete rest.description;
            raw.value = JSON.stringify(rest, null, 2);
            raw.setCustomValidity('');
          };
          // Raw JSON edits replace everything but title and notes once they parse into a
          // valid entry; the plugin fields are then rebuilt to match. Invalid JSON is
          // reported on the textarea and not saved.
          raw.oninput = () => {
            try {
              const value = JSON.parse(raw.value);
              const checked = stackEntry({ id: entry.id, metadata: value }, stackKeys);
              entry.metadata = {
                ...checked.metadata,
                title: entry.metadata.title,
                description: entry.metadata.description,
              };
              raw.setCustomValidity('');
              commit();
              renderPluginFields(
                other,
                plugins,
                entry.metadata,
                stackEditable,
                (key, value) => {
                  if (value) entry.metadata[key] = value;
                  else delete entry.metadata[key];
                  syncRaw();
                  commit();
                },
                openImage,
                confirmDelete,
                inheritedTime,
                true,
              );
            } catch {
              raw.setCustomValidity('Use a JSON metadata object without nested stacks.');
            }
          };
          details.append(summary, raw);
          card.append(details);
          list.append(card);
        });
        add.disabled = !stackEditable;
      };
      const add = document.createElement('button');
      add.type = 'button';
      add.textContent = 'Add entry to stack';
      add.onclick = () => {
        if (!stackEditable) return;
        entries.push({ id: crypto.randomUUID(), metadata: { title: '', description: '' } });
        commit();
        draw();
        list.lastElementChild?.querySelector('input')?.focus();
      };
      section.append(heading, unreadable, list, add);
      draw();
      container.append(section);
      continue;
    }
    const label = document.createElement('label');
    label.append(document.createTextNode(field.label));
    const input =
      field.kind === 'multiline'
        ? document.createElement('textarea')
        : document.createElement('input');
    input.value =
      typeof metadata[field.metadataKey] === 'string'
        ? (metadata[field.metadataKey] as string)
        : '';
    input.disabled = !editable;
    input.maxLength = field.kind === 'image-url' ? 4096 : 10000;
    input.spellcheck = field.kind !== 'image-url';
    input.dataset.pluginKey = field.metadataKey;
    if (input instanceof HTMLInputElement) {
      input.type = field.kind === 'image-url' ? 'url' : 'text';
      input.autocomplete = 'off';
    }
    /** For image fields: validates the URL and updates the picture, its link and its note. */
    let preview: ((immediate?: boolean) => void) | undefined;
    if (field.kind === 'image-url') {
      const link = document.createElement('a');
      link.className = 'plugin-image-link';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.referrerPolicy = 'no-referrer';
      link.title = 'Open original image in a new tab';
      const img = image(field.label);
      const sourceLabel = document.createElement('span');
      sourceLabel.className = 'plugin-image-source';
      sourceLabel.textContent = 'Open original image ↗';
      link.append(img, sourceLabel);
      const note = document.createElement('p');
      note.className = 'field-hint';
      let previewTimer: ReturnType<typeof setTimeout> | undefined;
      preview = (immediate = false) => {
        clearTimeout(previewTimer);
        const url = imageURL(input.value);
        input.setCustomValidity(
          input.value && !url ? 'Use a public HTTPS image URL without credentials.' : '',
        );
        link.hidden = !url;
        note.textContent =
          input.value && !url
            ? 'Use a public HTTPS image URL without credentials.'
            : editable
              ? 'Public HTTPS image, shown from where it is hosted: link only images you may share. The host must allow anonymous cross-origin image access.'
              : 'Shown from where it is hosted; its rights belong to its owner.';
        if (!url) {
          img.removeAttribute('src');
          link.removeAttribute('href');
          return;
        }
        if (imagesOffline()) link.removeAttribute('href');
        else link.href = url;
        // Its source, credited (#109): images are linked where their owners host them.
        sourceLabel.textContent = `Image from ${new URL(url).hostname} · open the original ↗`;
        // An embedded copy if the timeline has one; offline there is nothing else to show.
        const resolved = imageSource(url);
        if (!resolved) {
          img.hidden = true;
          img.removeAttribute('src');
          note.textContent =
            'No embedded image copy. Export this timeline from the online app to include its icon.';
          return;
        }
        // While typing, wait 300 ms and load only if the field is still on screen, so each
        // keystroke doesn't fetch a partial URL.
        if (img.getAttribute('src') !== resolved) {
          img.hidden = true;
          const load = () => {
            if (!immediate && (!section.isConnected || !section.getClientRects().length)) return;
            img.hidden = false;
            img.src = resolved;
          };
          if (immediate) load();
          else previewTimer = setTimeout(load, 300);
        }
      };
      // Events for an image the field no longer names are ignored.
      img.onerror = () => {
        if (img.getAttribute('src') !== imageSource(imageURL(input.value))) return;
        img.hidden = true;
        note.textContent =
          'Image could not load. Check the URL and the host’s cross-origin access settings.';
      };
      img.onload = () => {
        if (img.getAttribute('src') !== imageSource(imageURL(input.value))) return;
        img.hidden = false;
        note.textContent = imagesOffline()
          ? 'Embedded offline image. Its original URL is retained below.'
          : 'Click the image to open its original source.';
      };
      link.onclick = (event) => {
        const url = imageURL(input.value);
        if (url) openImage(event, url);
      };
      section.append(link);
      label.append(input);
      section.append(label, note);
      preview(true);
    } else {
      label.append(input);
      section.append(label);
    }
    input.oninput = () => {
      onChange(field.metadataKey, input.value);
      preview?.();
    };
    container.append(section);
  }
}

// SVG strokes provide a real white outline around polygons, rather than clipping a circular border.
const SHAPE_PATHS: Record<string, string> = {
  diamond: 'M12 1 L23 12 L12 23 L1 12 Z',
  square: 'M3 3 H21 V21 H3 Z',
  triangle: 'M12 2 L23 21 H1 Z',
  pentagon: 'M12 1 L23 9 L19 23 H5 L1 9 Z',
  hexagon: 'M6 2 H18 L23 12 L18 22 H6 L1 12 Z',
  octagon: 'M7 1 H17 L23 7 V17 L17 23 H7 L1 17 V7 Z',
  star: 'M12 1 L15 8 L23 9 L17 14 L19 23 L12 18 L5 23 L7 14 L1 9 L9 8 Z',
  terminator: 'M8 4 H16 A8 8 0 0 1 16 20 H8 A8 8 0 0 1 8 4 Z',
  process: 'M2 5 H22 V19 H2 Z',
  document: 'M2 3 H22 V19 Q17 15 12 19 Q7 23 2 19 Z',
  parallelogram: 'M7 4 H23 L17 20 H1 Z',
  ellipse: 'M0.5 12 A11.5 7 0 1 0 23.5 12 A11.5 7 0 1 0 0.5 12 Z',
};
/**
 * A moment shown as a heading (#110): its marker is its title, at the heading's level and in
 * its font; null restores the ordinary marker. Returns whether it is a heading.
 */
export function renderPluginHeading(
  button: HTMLButtonElement,
  heading: { level: HeadingLevel; font: string } | null,
  title: string,
): boolean {
  // The marker's class is rewritten as it renders; its data attribute says what it was.
  if (!heading) {
    if (button.dataset.heading) {
      button.classList.remove('heading-marker');
      delete button.dataset.heading;
      button.style.fontFamily = '';
      button.textContent = '';
    }
    return false;
  }
  button.classList.add('heading-marker');
  button.dataset.heading = heading.level;
  if (button.style.fontFamily !== heading.font) button.style.fontFamily = heading.font;
  const text = title.trim() || 'Untitled heading';
  if (button.textContent !== text || button.children.length) button.textContent = text;
  return true;
}
/**
 * Gives a marker its shape. A clip path (`--marker-clip`) shapes the marker's own
 * background and hit area; for any shape but a circle, two SVG copies of the outline are
 * added as well, a filled shape behind the content and a white-stroked border in front, so
 * polygons get a real outline. Previous shape elements are removed first.
 */
export function renderPluginShape(button: HTMLButtonElement, shape: string) {
  button.dataset.shape = shape;
  const clips: Record<string, string> = {
    diamond: 'polygon(50% 0,100% 50%,50% 100%,0 50%)',
    square: 'inset(0)',
    triangle: 'polygon(50% 0,100% 100%,0 100%)',
    pentagon: 'polygon(50% 0,100% 35%,82% 100%,18% 100%,0 35%)',
    hexagon: 'polygon(25% 0,75% 0,100% 50%,75% 100%,25% 100%,0 50%)',
    octagon: 'polygon(27% 0,73% 0,100% 27%,100% 73%,73% 100%,27% 100%,0 73%,0 27%)',
    star: 'polygon(50% 0,63% 32%,100% 36%,73% 59%,82% 100%,50% 77%,18% 100%,27% 59%,0 36%,37% 32%)',
    terminator: 'inset(15% 0 round 50%)',
    process: 'inset(14% 0)',
    document: 'polygon(0 0,100% 0,100% 85%,75% 75%,50% 85%,25% 100%,0 85%)',
    parallelogram: 'polygon(25% 0,100% 0,75% 100%,0 100%)',
    ellipse: 'ellipse(50% 30% at 50% 50%)',
  };
  button.style.setProperty('--marker-clip', clips[shape] ?? 'circle(50%)');
  button.querySelectorAll('.marker-shape').forEach((node) => node.remove());
  button.classList.toggle('shaped', shape !== 'circle');
  if (shape === 'circle') return;
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.classList.add('marker-shape');
  svg.setAttribute('viewBox', '-2 -2 28 28');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS(ns, 'path');
  path.setAttribute('d', SHAPE_PATHS[shape]);
  // The marker's color, or the stage's (#382), ringed in the stage's ground as circles are.
  path.style.fill = button.style.backgroundColor || 'var(--timeline-marker, var(--stage-marker))';
  path.style.stroke = 'var(--stage-ground)';
  path.setAttribute('stroke-linejoin', 'round');
  path.setAttribute('stroke-width', '5');
  const border = path.cloneNode(true) as SVGPathElement;
  border.style.fill = 'none';
  // A shallow clone keeps the `marker-shape` class, so the removal above clears it too.
  const outline = svg.cloneNode(false) as SVGSVGElement;
  outline.classList.add('marker-outline');
  outline.append(border);
  svg.append(path);
  button.querySelector('.marker-outline')?.remove();
  button.prepend(svg);
  button.append(outline);
}
/**
 * Outlines a marker (#325), after renderPluginShape has drawn it: dashed adds dashes in the
 * text's color over its ring in the stage's ground (dark over white, or light over dark, #382),
 * so the dashes show on light and dark backgrounds and against any color.
 * A circle (or an icon) dashes its CSS border (the `data-outline` attribute); any other shape
 * gets a dashed copy of its SVG outline's path, so every shape, present or future, is dashed
 * the same way. Solid removes them.
 */
export function renderPluginOutline(button: HTMLElement, outline: OutlineStyle) {
  button.querySelectorAll('.marker-dashes').forEach((node) => node.remove());
  if (outline !== 'dashed') {
    delete button.dataset.outline;
    return;
  }
  button.dataset.outline = 'dashed';
  const border = button.querySelector<SVGPathElement>('.marker-outline path');
  if (!border) return;
  const dashes = border.cloneNode(false) as SVGPathElement;
  dashes.classList.add('marker-dashes');
  dashes.style.removeProperty('stroke');
  dashes.removeAttribute('stroke-width');
  border.after(dashes);
}
